import { useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Loader2, AlertCircle, CheckCircle, ArrowLeft, ArrowRight, Home, Share2 } from "lucide-react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { getLoginUrl } from "@/const";

type FormData = {
  // Step 1 — Property Basics
  flatNumber: string;
  apartmentType: "flat" | "villa" | "studio" | "penthouse";
  bhk: string;
  areaInSqFt: string;
  floor: string;
  facing: string;
  parkingSlots: string;
  balconyCount: string;
  // Step 2 — Pricing & Availability
  listingType: "rent" | "sale";
  price: string;
  maintenanceIncluded: boolean;
  availabilityDate: string;
  expiryDays: string;
  // Step 3 — Furnishing & Amenities
  furnishingStatus: "fully_furnished" | "semi_furnished" | "unfurnished" | "";
  amenities: string[];
  // Step 4 — Photos
  photoUrls: string;
  // Step 5 — Preferred Tenant & Description
  prefFamilyOnly: boolean;
  prefNoPets: boolean;
  prefVegetarian: boolean;
  prefWorkingProfOnly: boolean;
  prefNotes: string;
  description: string;
  // Step 6 — Contact
  residentPhone: string;
  residentEmail: string;
};

const AMENITIES = [
  "24-hour Power Backup", "Swimming Pool", "Gym", "Parking", "Garden",
  "Security", "Lift", "Balcony", "Modular Kitchen", "Air Conditioning",
  "Water Purifier", "CCTV", "Intercom", "Visitor Parking", "Clubhouse",
];

const STEPS = [
  "Property Basics",
  "Pricing & Availability",
  "Furnishing & Amenities",
  "Photos",
  "Tenant Preferences",
  "Contact & Review",
];

const EMPTY: FormData = {
  flatNumber: "", apartmentType: "flat", bhk: "2", areaInSqFt: "", floor: "", facing: "",
  parkingSlots: "", balconyCount: "",
  listingType: "rent", price: "", maintenanceIncluded: false,
  availabilityDate: "", expiryDays: "90",
  furnishingStatus: "", amenities: [],
  photoUrls: "",
  prefFamilyOnly: false, prefNoPets: false, prefVegetarian: false,
  prefWorkingProfOnly: false, prefNotes: "", description: "",
  residentPhone: "", residentEmail: "",
};

export default function PostApartment() {
  const [, navigate] = useLocation();
  const { user, isAuthenticated, loading } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormData>(EMPTY);
  const [result, setResult] = useState<{ listingId: number; listingCode: string } | null>(null);

  const createMutation = trpc.apartments.create.useMutation();

  const set = (patch: Partial<FormData>) => setForm((f) => ({ ...f, ...patch }));

  const toggleAmenity = (a: string) =>
    set({ amenities: form.amenities.includes(a) ? form.amenities.filter((x) => x !== a) : [...form.amenities, a] });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin w-8 h-8 text-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12">
        <div className="container mx-auto px-4">
          <Card className="max-w-md mx-auto p-8 text-center">
            <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Login Required</h2>
            <p className="text-slate-600 mb-6">You need to be logged in to post a listing.</p>
            <Button onClick={() => (window.location.href = getLoginUrl("/post-apartment"))} className="w-full bg-blue-600 hover:bg-blue-700">
              Login to Post
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  // ── Success screen ──────────────────────────────────────────────────────────
  if (result) {
    const shareText = encodeURIComponent(
      `🏠 ${form.bhk} BHK ${form.apartmentType} for ${form.listingType} in Purva Swanlake\n` +
      `Flat ${form.flatNumber} | ₹${parseInt(form.price).toLocaleString("en-IN")}${form.listingType === "rent" ? "/mo" : ""}\n` +
      `Listing ID: ${result.listingCode}\n` +
      `View: ${window.location.origin}/apartments/${result.listingId}`
    );
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center py-12">
        <Card className="max-w-md mx-auto p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-1">Listing Published!</h2>
          <p className="text-slate-500 mb-4">Your listing is now live on the marketplace.</p>
          <div className="bg-slate-100 rounded-lg px-4 py-3 mb-6">
            <p className="text-sm text-slate-500 mb-1">Listing Code</p>
            <p className="text-2xl font-mono font-bold text-slate-900">{result.listingCode}</p>
          </div>
          <div className="flex flex-col gap-3">
            <a
              href={`https://wa.me/?text=${shareText}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Share on WhatsApp
            </a>
            <Button onClick={() => navigate(`/apartments/${result.listingId}`)} className="bg-blue-600 hover:bg-blue-700">
              View My Listing
            </Button>
            <Button variant="outline" onClick={() => { setResult(null); setStep(0); setForm(EMPTY); }}>
              Post Another Listing
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // ── Validation per step ─────────────────────────────────────────────────────
  const canNext = () => {
    switch (step) {
      case 0: return form.flatNumber.trim().length > 0 && form.areaInSqFt.trim().length > 0;
      case 1: return form.price.trim().length > 0 && form.availabilityDate.length > 0;
      case 2: return true;
      case 3: return true;
      case 4: return form.description.trim().length >= 10;
      case 5: return form.residentPhone.trim().length >= 10 && form.residentEmail.includes("@");
      default: return true;
    }
  };

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const photos = form.photoUrls
      .split("\n")
      .map((u) => u.trim())
      .filter(Boolean);

    try {
      const res = await createMutation.mutateAsync({
        flatNumber: form.flatNumber.trim().toUpperCase(),
        apartmentType: form.apartmentType,
        bhk: parseInt(form.bhk),
        listingType: form.listingType,
        price: parseInt(form.price),
        areaInSqFt: parseInt(form.areaInSqFt),
        floor: form.floor ? parseInt(form.floor) : undefined,
        facing: form.facing || undefined,
        parkingSlots: form.parkingSlots ? parseInt(form.parkingSlots) : undefined,
        balconyCount: form.balconyCount ? parseInt(form.balconyCount) : undefined,
        amenities: form.amenities,
        description: form.description,
        photos,
        availabilityDate: form.availabilityDate,
        expiryDays: parseInt(form.expiryDays),
        residentPhone: form.residentPhone,
        residentEmail: form.residentEmail,
        furnishingStatus: form.furnishingStatus || undefined,
        maintenanceIncluded: form.maintenanceIncluded ? 1 : 0,
        preferredTenant: {
          familyOnly: form.prefFamilyOnly || undefined,
          noPets: form.prefNoPets || undefined,
          vegetarian: form.prefVegetarian || undefined,
          workingProfOnly: form.prefWorkingProfOnly || undefined,
          notes: form.prefNotes || undefined,
        },
      });
      setResult({ listingId: res.listingId, listingCode: res.listingCode });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create listing");
    }
  };

  // ── Step content ────────────────────────────────────────────────────────────
  const renderStep = () => {
    switch (step) {
      // ── Step 1: Property Basics ──
      case 0:
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Flat Number *</label>
              <Input
                placeholder="e.g. A-101, B-1202"
                value={form.flatNumber}
                onChange={(e) => set({ flatNumber: e.target.value })}
              />
              <p className="text-xs text-slate-500 mt-1">Must be a valid Purva Swanlake flat (A-101 format).</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Apartment Type *</label>
                <Select value={form.apartmentType} onValueChange={(v) => set({ apartmentType: v as any })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">Flat</SelectItem>
                    <SelectItem value="villa">Villa</SelectItem>
                    <SelectItem value="studio">Studio</SelectItem>
                    <SelectItem value="penthouse">Penthouse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">BHK *</label>
                <Select value={form.bhk} onValueChange={(v) => set({ bhk: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 BHK</SelectItem>
                    <SelectItem value="2">2 BHK</SelectItem>
                    <SelectItem value="3">3 BHK</SelectItem>
                    <SelectItem value="4">4 BHK</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Area (sq ft) *</label>
                <Input type="number" placeholder="e.g. 1250" value={form.areaInSqFt} onChange={(e) => set({ areaInSqFt: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Floor</label>
                <Input type="number" placeholder="e.g. 5" value={form.floor} onChange={(e) => set({ floor: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Facing</label>
                <Select value={form.facing || "none"} onValueChange={(v) => set({ facing: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Select facing" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    <SelectItem value="North">North</SelectItem>
                    <SelectItem value="South">South</SelectItem>
                    <SelectItem value="East">East</SelectItem>
                    <SelectItem value="West">West</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Parking Slots</label>
                <Input type="number" placeholder="0" value={form.parkingSlots} onChange={(e) => set({ parkingSlots: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Balconies</label>
                <Input type="number" placeholder="0" value={form.balconyCount} onChange={(e) => set({ balconyCount: e.target.value })} />
              </div>
            </div>
          </div>
        );

      // ── Step 2: Pricing & Availability ──
      case 1:
        return (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Listing Type *</label>
                <Select value={form.listingType} onValueChange={(v) => set({ listingType: v as any })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rent">For Rent</SelectItem>
                    <SelectItem value="sale">For Sale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Price (₹) {form.listingType === "rent" ? "/month" : ""} *
                </label>
                <Input type="number" placeholder="e.g. 25000" value={form.price} onChange={(e) => set({ price: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
              <input
                type="checkbox"
                id="maint"
                checked={form.maintenanceIncluded}
                onChange={(e) => set({ maintenanceIncluded: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="maint" className="text-sm font-medium text-slate-700 cursor-pointer">
                Maintenance charges included in the price
              </label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Available From *</label>
                <Input type="date" value={form.availabilityDate} onChange={(e) => set({ availabilityDate: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">List for</label>
                <Select value={form.expiryDays} onValueChange={(v) => set({ expiryDays: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="60">60 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                    <SelectItem value="180">180 days</SelectItem>
                    <SelectItem value="365">1 year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      // ── Step 3: Furnishing & Amenities ──
      case 2:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">Furnishing Status</label>
              <div className="grid grid-cols-3 gap-3">
                {(["fully_furnished", "semi_furnished", "unfurnished"] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => set({ furnishingStatus: v })}
                    className={`border-2 rounded-lg p-3 text-sm font-medium transition-colors text-center ${
                      form.furnishingStatus === v
                        ? "border-blue-600 bg-blue-50 text-blue-800"
                        : "border-slate-200 text-slate-700 hover:border-slate-300"
                    }`}
                  >
                    {v === "fully_furnished" ? "Fully Furnished" : v === "semi_furnished" ? "Semi Furnished" : "Unfurnished"}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">Amenities</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {AMENITIES.map((a) => (
                  <label key={a} className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={form.amenities.includes(a)}
                      onChange={() => toggleAmenity(a)}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm text-slate-700">{a}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      // ── Step 4: Photos ──
      case 3:
        return (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                Upload photos to Google Drive or Google Photos and paste the direct/shareable links below — one per line.
                High-quality photos significantly increase interest.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Photo URLs (one per line)</label>
              <Textarea
                rows={8}
                placeholder={`https://drive.google.com/...\nhttps://drive.google.com/...\nhttps://photos.google.com/...`}
                value={form.photoUrls}
                onChange={(e) => set({ photoUrls: e.target.value })}
              />
            </div>
            {form.photoUrls.split("\n").filter(Boolean).length > 0 && (
              <div>
                <p className="text-sm text-slate-500 mb-2">{form.photoUrls.split("\n").filter(Boolean).length} photo link(s) added</p>
                <div className="flex flex-wrap gap-2">
                  {form.photoUrls.split("\n").filter(Boolean).slice(0, 5).map((url, i) => (
                    <div key={i} className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                      <img
                        src={url.trim()}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      // ── Step 5: Preferred Tenant & Description ──
      case 4:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">Preferred Tenant Profile</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: "prefFamilyOnly", label: "Families only" },
                  { key: "prefNoPets", label: "No pets" },
                  { key: "prefVegetarian", label: "Vegetarian preferred" },
                  { key: "prefWorkingProfOnly", label: "Working professionals" },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer select-none bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <input
                      type="checkbox"
                      checked={!!(form as any)[key]}
                      onChange={(e) => set({ [key]: e.target.checked } as any)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-slate-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Additional notes on tenant preferences</label>
              <Input
                placeholder="e.g. Professionals working in nearby tech parks preferred"
                value={form.prefNotes}
                onChange={(e) => set({ prefNotes: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Description *</label>
              <Textarea
                rows={5}
                placeholder="Describe the apartment — renovation status, views, condition, what makes it special..."
                value={form.description}
                onChange={(e) => set({ description: e.target.value })}
              />
              <p className="text-xs text-slate-500 mt-1">{form.description.length} characters (min 10)</p>
            </div>
          </div>
        );

      // ── Step 6: Contact & Review ──
      case 5:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number *</label>
                <Input type="tel" placeholder="+91 XXXXX XXXXX" value={form.residentPhone} onChange={(e) => set({ residentPhone: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email Address *</label>
                <Input type="email" placeholder="your@email.com" value={form.residentEmail} onChange={(e) => set({ residentEmail: e.target.value })} />
              </div>
            </div>

            {/* Summary card */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 space-y-3">
              <h3 className="font-semibold text-slate-900 mb-3">Listing Summary</h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div><span className="text-slate-500">Flat</span><p className="font-medium text-slate-900">{form.flatNumber || "—"}</p></div>
                <div><span className="text-slate-500">Type</span><p className="font-medium text-slate-900 capitalize">{form.bhk} BHK {form.apartmentType}</p></div>
                <div><span className="text-slate-500">Area</span><p className="font-medium text-slate-900">{form.areaInSqFt ? `${form.areaInSqFt} sq ft` : "—"}</p></div>
                <div><span className="text-slate-500">Listing</span><p className="font-medium text-slate-900 capitalize">{form.listingType}</p></div>
                <div><span className="text-slate-500">Price</span><p className="font-medium text-slate-900">₹{parseInt(form.price || "0").toLocaleString("en-IN")}{form.listingType === "rent" ? "/mo" : ""}</p></div>
                <div><span className="text-slate-500">Available</span><p className="font-medium text-slate-900">{form.availabilityDate || "—"}</p></div>
                <div><span className="text-slate-500">Furnishing</span><p className="font-medium text-slate-900 capitalize">{form.furnishingStatus?.replace(/_/g, " ") || "Not specified"}</p></div>
                <div><span className="text-slate-500">Photos</span><p className="font-medium text-slate-900">{form.photoUrls.split("\n").filter(Boolean).length} added</p></div>
                <div><span className="text-slate-500">Amenities</span><p className="font-medium text-slate-900">{form.amenities.length} selected</p></div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800">
                Your listing publishes immediately and is visible to all Purva Swanlake residents.
                RWA admins may contact you for verification.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Nav */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => window.history.back()} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <Home className="w-4 h-4" />
            Home
          </Button>
        </div>
      </div>

      <div className="py-12">
        <div className="container mx-auto px-4 max-w-2xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900 mb-1">Post Your Apartment</h1>
            <p className="text-slate-500">List your apartment in the Purva Swanlake Marketplace</p>
          </div>

          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>Step {step + 1} of {STEPS.length}</span>
              <span>{STEPS[step]}</span>
            </div>
            <Progress value={((step + 1) / STEPS.length) * 100} className="h-2" />
            <div className="flex justify-between mt-2">
              {STEPS.map((s, i) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => i < step && setStep(i)}
                  className={`text-xs font-medium transition-colors ${
                    i === step ? "text-blue-600" : i < step ? "text-slate-500 cursor-pointer hover:text-blue-600" : "text-slate-300 cursor-default"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </div>

          {/* Step card */}
          <Card className="p-6 md:p-8 mb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-5">{STEPS[step]}</h2>
            {renderStep()}
          </Card>

          {/* Navigation buttons */}
          <div className="flex gap-3">
            {step > 0 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canNext()}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={createMutation.isPending || !canNext()}
                className="flex-1 bg-blue-600 hover:bg-blue-700 h-12 text-base"
              >
                {createMutation.isPending ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Publishing...</>
                ) : (
                  <><CheckCircle className="w-5 h-5 mr-2" />Publish Listing</>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
